/*
 * The states that separate a real app from a demo.
 *
 * A prototype has one state: everything loaded, nothing wrong. Production has
 * five, and a teacher in a shop with two bars of signal will meet all of
 * them. Each one here says what happened and what to do about it — never
 * just "Error".
 */

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" style={{ display: 'grid', gap: 'var(--s-2)' }}>
      <span className="visually-hidden">{label}</span>
      <div className="skeleton" style={{ height: 64 }} />
      <div className="skeleton" style={{ height: 64 }} />
      <div className="skeleton" style={{ height: 64, opacity: 0.6 }} />
    </div>
  )
}

export function Empty({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="state">
      <div className="state__title">{title}</div>
      <p className="state__body">{body}</p>
      {action}
    </div>
  )
}

export function ErrorState({ title = 'That did not go through', body, onRetry }: {
  title?: string; body: string; onRetry?: () => void
}) {
  return (
    <div className="state" role="alert">
      <div className="state__title">{title}</div>
      <p className="state__body">{body}</p>
      {onRetry && <button className="btn btn--ghost" onClick={onRetry}>Try again</button>}
    </div>
  )
}

/**
 * Shown while a submission sits in the offline queue. The rule this enforces:
 * something that has not left the phone must never be displayed as saved.
 * Somebody who believes it went through, when it did not, is worse off than
 * somebody who knows to follow up.
 */
export function QueuedOffline({ count }: { count: number }) {
  return (
    <div className="notice notice--warning" role="status">
      <span aria-hidden="true">⚠</span>
      <div>
        <strong>{count === 1 ? 'This change is' : `${count} changes are`} saved on this device
        but not sent yet.</strong>{' '}
        {count === 1 ? 'It will send' : 'They will send'} automatically when you have signal.
      </div>
    </div>
  )
}

export function Denied({ what = 'this' }: { what?: string }) {
  return (
    <div className="state">
      <div className="state__title">You do not have access to {what}</div>
      <p className="state__body">
        Access is set by your building admin. If you think this is wrong, ask them
        to add you. It takes about ten seconds on their end.
      </p>
    </div>
  )
}
